import dayjs from 'dayjs';
import type { EmberChangeset } from 'ember-changeset';
import {
  validateDate,
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { ValidatorMap } from 'ember-changeset/types';
import type { ForcModelPojo } from 'parog-web/components/forc/abstract/journal-event-based-component';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import DiabetoEditionForm from 'parog-web/components/medical-history/diabeto/diabeto-edition-form';
import validateSometimes from 'parog-web/validators/sometimes';
import emberSwal from 'ui-kit/utils/ember-swal';
export default class MedicalHistoryFormComponent extends BaseMedicalHistoryFormComponent {
  static override forcDisplayName = 'Profil médical – formulaire d’inclusion';

  override journalEventProperties = [
    ...this.sharedProperties,
    ...this.diabetePathologyProperties
  ];

  get age(): number | undefined {
    return this.changeset.get('age');
  }

  get diabetePathologyProperties(): string[] {
    return [
      'birthday',
      'state',
      'height',
      'weight',
      'year',
      'hba',
      'hbaDate',
      'mrc',
      'dfg',
      'rac',
      'reti',
      'neur',
      'mcva',
      'ic',
      'oth',
      'exp',
      'hta',
      'met',
      'isglt2',
      'arglp1',
      'fevg',
      'systolic',
      'diastolic',
      'dyslipidemieTreated',
      'lastDyslipidemie',
      'smoker',
      'doSport',
      'insulin',
      'idpp4',
      'familyIcBackground',
      'albuminuria',
      'isFollowingDiabetologist'
    ];
  }

  get isMissingInformations(): boolean {
    const comorbidities = this.comorbiditiesChangeset?.get('comorbidities');
    if (!comorbidities) return false;
    if (comorbidities.includes('N18.9')) {
      if (!this.changeset.get('dfg')) return true;
      if (!this.changeset.get('rac')) return true;
      if (!this.changeset.get('albuminuria')) return true;
    }
    if (comorbidities.includes('I50.9')) {
      if (!this.changeset.get('fevg')) return true;
    }
    if (comorbidities.includes('I10'))
      if (this.changeset.get('hta') == undefined) return true;
    if (comorbidities.includes('E78.9'))
      if (this.changeset.get('dyslipidemieTreated') == undefined) return true;

    return false;
  }

  override get validations(): ValidatorMap {
    const isGeneric = () => {
      return (
        this.enrollmentChangeset.get('followUpReason') != 'diabeto-generic'
      );
    };

    const validatesIfNotGeneric = validator => {
      return validateSometimes(validator, isGeneric);
    };

    return {
      birthday: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'La date de naissance du patient'
          })
        ),
        validateDate({
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900'),
          allowBlank: true
        }),
        validateDate({
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate(),
          allowBlank: true
        })
      ],
      state: validatesIfNotGeneric(
        validateSometimes(
          [
            validatePresence({
              presence: true,
              message: "L'état de santé du patient"
            })
          ],
          function (this: EmberChangeset) {
            return (this as EmberChangeset).get('age') >= 75;
          }
        )
      ),
      height: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'La taille du patient'
          })
        )
      ],
      weight: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Le poid du patient'
          })
        )
      ],
      year: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: "L'année du diagnostic du diabète"
          })
        ),
        validateNumber({
          gte: 1900,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          allowBlank: true
        }),
        validateNumber({
          lte: 2025,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          allowBlank: true
        })
      ],
      hba: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Dernier HBA1c connu'
          })
        )
      ],
      hbaDate: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: "Date de la dernière mesure d'HBA1c"
          })
        ),
        validateDate({
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900'),
          allowBlank: true
        }),
        validateDate({
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate(),
          allowBlank: true
        })
      ],
      systolic: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description:
              'La dernière pression artérielle systolique en consultation'
          })
        )
      ],
      diastolic: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description:
              'La dernière pression artérielle diastolique en consultation'
          })
        )
      ],
      lastDyslipidemie: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'La dernière mesure de LDLc'
          })
        )
      ],
      met: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Metformine'
          })
        )
      ],
      isglt2: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'ISGLT2'
          })
        )
      ],
      arglp1: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'AR GLP-1'
          })
        )
      ],
      smoker: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Fumeur'
          })
        )
      ],
      doSport: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Activités sportives'
          })
        )
      ],
      insulin: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'Insuline'
          })
        )
      ],
      idpp4: [
        validatesIfNotGeneric(
          validatePresence({
            presence: true,
            description: 'iDDP4'
          })
        )
      ]
    };
  }

  override retrieveModel(): ForcModelPojo {
    const model = super.retrieveModel();
    if (this.isNewMode) {
      model.birthday = this.args.patient.birthday;
      model.age = this.args.patient.patientAge;
    }
    return model;
  }

  override async save(): Promise<void> {
    if (this.isMissingInformations) {
      const response = await emberSwal.fire({
        title: 'Êtes-vous sûr de vouloir sauvegarder ?',
        text: 'Des éléments complémentaires ne sont pas renseignés au niveau des comorbidités et pourraient minorer la recommendation obtenue pour ce patient',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        showCancelButton: true,
        confirmButtonText: 'Oui',
        confirmButtonColor: '#d33'
      });

      if (response.isDismissed) {
        this.afterCompleted = () => {
          this.shouldForceQuit = false;
        };
        return;
      }
    }

    this.afterCompleted = this.args.afterCompleted
      ? this.args.afterCompleted
      : () => {
          return;
        };

    const comorbidities = this.comorbiditiesChangeset.get('comorbidities');
    const ignoredComorbidities = this.comorbiditiesChangeset.get(
      'ignoredComorbidities'
    );
    const keysToCode = {
      reti: 'H36',
      neur: 'G99.0',
      mrc: 'N18.9',
      mcva: 'I51.6',
      ic: 'I50.9',
      oth: 'other'
    };
    for (const [key, code] of Object.entries(keysToCode)) {
      this.changeset.set(
        key,
        comorbidities?.includes(code)
          ? 'true'
          : ignoredComorbidities?.includes(code)
            ? 'false'
            : 'unknown'
      );
    }

    const htaValue = !comorbidities?.includes('I10')
      ? ignoredComorbidities?.includes('I10')
        ? 'no'
        : 'unknown'
      : this.changeset.get('hta') ?? 'treated';

    this.changeset.set('hta', htaValue);

    if (this.changeset.get('birthday') !== this.patient.birthday) {
      this.patient.birthday = this.changeset.get('birthday');
      await this.patient.save();
    }

    await super.save();
  }

  updateAge = value => {
    this.changeset.set('birthday', value);
    this.changeset.set(
      'age',
      dayjs().diff(this.changeset.get('birthday'), 'years')
    );
  };

  <template>
    <DiabetoEditionForm @forcComponent={{this}} @patient={{@patient}} />
  </template>
}
